import { availablePaymentMethods, init, PaymentMethodType } from '@nemuru-eng/checkout-js';
import { getLocaleByPurchaseCountry, PurchaseCountry } from '@nemuru-eng/js-api-client';

import { AbstractPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { GetPricingsService } from '@/components/modules/Application/components/PaymentMethod/_shared/services/GetPricingsService';
import { ENV_ENV } from '@/config/envVars';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

const pricingsService = GetPricingsService.getInstance();

export const usePaymentMethods = () => {
  const {
    state: { cart, paymentMethodEntities, selectedAgentHasError, downPayment },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const totalToFinance = (cart?.total() || 0) - (cart?.totalDiscount() || 0) - downPayment.amount.amount;

  const updateAgentPaymentConfiguration = async (agentId: string) => {
    try {
      await init(agentId, {
        env: ENV_ENV,
        locale: getLocaleByPurchaseCountry(cart?.purchaseCountry ?? PurchaseCountry.ESP),
      });
      updateApplicationContext({ selectedAgentHasError: false });
    } catch (err) {
      updateApplicationContext({ selectedAgentHasError: true });
      console.error(`failed to initialize checkout-js with agentId: ${agentId}`);
      return;
    }
    try {
      await pricingsService.execute(agentId);
      updateApplicationContext({ selectedAgentHasError: false });
    } catch (err) {
      updateApplicationContext({ selectedAgentHasError: true });
      console.error('Failed to fetch pricings', err);
    }
  };

  const createPaymentMethodEntities = () => {
    if (!cart) return;

    if (
      paymentMethodEntities &&
      paymentMethodEntities?.length > 0 &&
      totalToFinance === paymentMethodEntities[0].amount
    ) {
      return;
    }

    const paymentMethodTypes = availablePaymentMethods({ amount: totalToFinance });

    if (!paymentMethodTypes) return;

    // Remove PaymentMethodType.CARD_PAYMENT if total amount is larger than 5000
    const filterdPaymentMethodTypes = filterSingleCardPayment(paymentMethodTypes, totalToFinance);

    const newPaymentMethodEntities: AbstractPaymentMethod[] = [];
    for (const paymentMethodType of filterdPaymentMethodTypes) {
      const paymentMethodEntity = PaymentMethodFactory.byType(paymentMethodType);
      if (paymentMethodEntity) {
        newPaymentMethodEntities.push(paymentMethodEntity);
      }
    }

    if (newPaymentMethodEntities.length === 0) {
      console.error(
        new Error(
          `There are no payment method types available because the available payment methods for this agent and amount (${JSON.stringify(
            paymentMethodTypes,
          )}) are not implemented in Nemuru console yet. Try with another amount or another agent.`,
        ),
      );
    }
    updateApplicationContext({ paymentMethodEntities: newPaymentMethodEntities });
  };

  const updatePaymentMethodEntity = (paymentMethodEntity: AbstractPaymentMethod) => {
    // Replace in paymentMethodEntities array the paymentMethodEntity using the paymentMethodType, and updateApplicationContext
    if (!paymentMethodEntities) return;
    const updatedPaymentMethodEntities = paymentMethodEntities.map((entity) =>
      entity.paymentMethodType === paymentMethodEntity.paymentMethodType ? paymentMethodEntity : entity,
    );
    updateApplicationContext({ paymentMethodEntities: updatedPaymentMethodEntities });
  };

  const setupPaymentMethodEntity = (paymentMethodEntity: AbstractPaymentMethod) => {
    if (!cart) return;
    paymentMethodEntity.setup(totalToFinance, cart.currency);
    updatePaymentMethodEntity(paymentMethodEntity);
  };

  const areAllPaymentMethodEntitiesSetup = paymentMethodEntities?.every(
    (paymentMethodEntity) => paymentMethodEntity.isSetup,
  );

  const arePaymentMethodsAvailable = (amount: number): { state: boolean; message: string } => {
    if (!amount || selectedAgentHasError)
      return {
        state: false,
        message: '',
      };
    try {
      const paymentMethodTypes = availablePaymentMethods({ amount });
      if (paymentMethodTypes && paymentMethodTypes.length > 0) {
        return {
          state: true,
          message: '',
        };
      } else {
        return {
          state: false,
          message: 'CART_ALERT_PAYMENT_METHODS',
        };
      }
    } catch {
      console.error(
        new Error(
          'Agent configuration mismatch. Try updating pricings in CDN then syncing agent configuration through syncAgentConfigurationToCdn',
        ),
      );
      return {
        state: false,
        message: 'CART_ALERT_AGENT_CONFIGURATION',
      };
    }
  };

  const selectedPaymentMethods: AbstractPaymentMethod[] | undefined = paymentMethodEntities?.filter(
    (paymentMethodEntity) => paymentMethodEntity.isSelected,
  );

  const isAnyPaymentMethodSelectedAndConfigured = paymentMethodEntities?.some((paymentMethodEntity) => {
    const isSelected = paymentMethodEntity.isSelected;
    const areOptionsConfigurable = paymentMethodEntity.areOptionsConfigurable();
    const selectedConditionsGroup = paymentMethodEntity.selectedConditionsGroup;

    if (areOptionsConfigurable) {
      return isSelected && selectedConditionsGroup;
    }
    return isSelected;
  });

  return {
    currency: cart?.currency,
    paymentMethodEntities,
    createPaymentMethodEntities,
    setupPaymentMethodEntity,
    updatePaymentMethodEntity,
    updateAgentPaymentConfiguration,
    areAllPaymentMethodEntitiesSetup,
    arePaymentMethodsAvailable,
    selectedPaymentMethods,
    selectedAgentHasError,
    isAnyPaymentMethodSelectedAndConfigured,
    totalToFinance,
  };
};

const filterSingleCardPayment = (paymentMethodTypes: PaymentMethodType[], amount: number): PaymentMethodType[] => {
  return paymentMethodTypes.filter((paymentMethodType) => {
    if (paymentMethodType === PaymentMethodType.CARD_PAYMENT) return amount <= 3000;
    if (paymentMethodType === PaymentMethodType.A2A_PAYMENT) return amount <= 10000;
    return true;
  });
};
